import clsx from 'clsx';

/**
 * Button - Genel amaçlı reusable buton bileşeni
 * 
 * @param {string} variant - Buton varyantı
 * @param {string} size - Buton boyutu
 * @param {boolean} isLoading - Yükleme durumu
 * @param {boolean} disabled - Devre dışı durumu
 * @param {boolean} fullWidth - Tam genişlik
 * @param {ReactNode} icon - İkon bileşeni
 * @param {string} iconPosition - İkon pozisyonu
 */
export default function Button({
    children,
    type = 'button',
    variant = 'primary',
    size = 'md',
    isLoading = false,
    disabled = false,
    fullWidth = false,
    icon,
    iconPosition = 'left',
    className = '',
    onClick,
    form,
    ...props
}) {
    const isDisabled = disabled || isLoading;

    // Buton varyantları - İNSANET renk paleti
    const variantClasses = {
        // Ana varyantlar
        primary: 'bg-primary hover:bg-primary-dark text-white shadow-sm',
        secondary: 'bg-secondary hover:bg-secondary-dark text-white shadow-sm',
        tertiary: 'bg-tertiary hover:bg-tertiary-dark text-white shadow-sm',

        // Durum varyantları
        success: 'bg-success hover:bg-success-dark text-white shadow-sm',
        warning: 'bg-warning hover:bg-warning-dark text-white shadow-sm',
        error: 'bg-error hover:bg-error-dark text-white shadow-sm',
        danger: 'bg-error hover:bg-error-dark text-white shadow-sm',
        info: 'bg-info hover:bg-info-dark text-white shadow-sm',

        // Outline varyantları
        'outline-primary': 'bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white',
        'outline-secondary': 'bg-transparent border-2 border-secondary text-secondary hover:bg-secondary hover:text-white',
        'outline-tertiary': 'bg-transparent border-2 border-tertiary text-tertiary hover:bg-tertiary hover:text-white',
        'outline-success': 'bg-transparent border-2 border-success text-success hover:bg-success hover:text-white',
        'outline-error': 'bg-transparent border-2 border-error text-error hover:bg-error hover:text-white',

        // Ghost varyantları
        'ghost': 'bg-transparent hover:bg-bg-hover text-text-secondary',
        'ghost-primary': 'bg-transparent hover:bg-primary-100 text-primary',
        'ghost-secondary': 'bg-transparent hover:bg-secondary-100 text-secondary',
        'ghost-error': 'bg-transparent hover:bg-error-100 text-error',

        // Beyaz/ters varyantlar
        white: 'bg-white hover:bg-neutral-50 text-primary border border-border-light shadow-sm',

        // Soft varyantlar (açık arka plan)
        'soft-primary': 'bg-primary-100 hover:bg-primary-200 text-primary-dark',
        'soft-secondary': 'bg-secondary-100 hover:bg-secondary-200 text-secondary-dark',
        'soft-tertiary': 'bg-tertiary-100 hover:bg-tertiary-200 text-tertiary-dark',
        'soft-success': 'bg-success-100 hover:bg-success-200 text-success-dark',
        'soft-warning': 'bg-warning-100 hover:bg-warning-200 text-warning-dark',
        'soft-error': 'bg-error-100 hover:bg-error-200 text-error-dark',
        'soft-info': 'bg-info-100 hover:bg-info-200 text-info-dark',

        // İkon butonu
        icon: 'bg-bg-hover hover:bg-bg-active text-text-secondary p-0',
    };

    // Buton boyutları
    const sizeClasses = {
        xs: 'h-7 px-2.5 text-xs gap-1',
        sm: 'h-8 px-3 text-sm gap-1.5',
        md: 'h-10 px-4 text-sm gap-2',
        lg: 'h-12 px-6 text-base gap-2',
        xl: 'h-14 px-8 text-lg gap-2.5',
    };

    // İkon butonu için özel boyutlar
    const iconSizeClasses = {
        xs: 'h-7 w-7',
        sm: 'h-8 w-8',
        md: 'h-10 w-10',
        lg: 'h-12 w-12',
        xl: 'h-14 w-14',
    };

    const isIconVariant = variant === 'icon';

    return (
        <button
            type={type}
            form={form}
            className={clsx(
                'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50',
                'active:scale-[0.98]',
                variantClasses[variant],
                isIconVariant ? iconSizeClasses[size] : sizeClasses[size],
                fullWidth && 'w-full',
                isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
                className
            )}
            disabled={isDisabled}
            onClick={onClick}
            {...props}
        >
            {isLoading ? (
                <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                            className="opacity-25"
                            cx="12" cy="12" r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                    <span>Yükleniyor...</span>
                </>
            ) : (
                <>
                    {icon && iconPosition === 'left' && (
                        <span className="flex-shrink-0">{icon}</span>
                    )}
                    {children}
                    {icon && iconPosition === 'right' && (
                        <span className="flex-shrink-0">{icon}</span>
                    )}
                </>
            )}
        </button>
    );
}
